import {
  type DateRange,
  type DownloadFileResponse,
  type FailedResponse,
  type SuccessResponseWithoutPayload
} from './types'
import { ApiUtil } from './api-utils'
import {
  mapAdminCompaniesResponse,
  mapAdminCompanyIntegrationResponse,
  mapAdminCompanyResponse,
  mapAdminCompanyUserResponse,
  mapAdminCompanyUsersResponse,
  mapAdminUnassignedUsersResponse
} from './response-mappers'
import {
  type AdminCompanyIntegrationResponse,
  type AdminCompanyResponse,
  type AdminCompanyUserResponse,
  CompanyIntegrationType,
  type UnassignedUserResponseItem
} from './admin-companies-api-types'
import { CsvType } from '../constants/csv-type'

/* API related constants */

const ADMIN_COMPANY_API_PATH = '/admin/companies'

/* Request types */

/**
 * Payload format matching create and update company backend API's request data type ('CompanyUpdateRequestDto')
 */
export interface UpdateCompanyRequestPayload {
  name: string
  vatId: string
  streetAddress: string
  postalCode: string
  town: string
}

export interface UpdateCompanyIntegrationPayload {
  enabled: boolean
  integrationType: CompanyIntegrationType
  cronString: string
  url: string
  username?: string
  password?: string
  inPulseFormat?: string
  tietoIdentifier?: string
}

/* Response types */

interface UpdateCompanySuccessResponse {
  succeeded: true
  payload: {
    company: AdminCompanyResponse
  }
}

export type UpdateCompanyResponse = UpdateCompanySuccessResponse | FailedResponse

export type CompanyDeleteResponse = SuccessResponseWithoutPayload | FailedResponse

interface CompanyIntegrationSuccessResponse {
  succeeded: true
  payload: {
    integration: AdminCompanyIntegrationResponse
  }
}

export type CompanyIntegrationResponse = CompanyIntegrationSuccessResponse | FailedResponse

export type CompanyIntegrationDeleteResponse = SuccessResponseWithoutPayload | FailedResponse

interface CompanyUserAssignationSuccessResponse {
  succeeded: true
  payload: {
    user: AdminCompanyUserResponse
  }
}

export type CompanyUserAssignationResponse = CompanyUserAssignationSuccessResponse | FailedResponse

export type CompanyUserDeleteResponse = SuccessResponseWithoutPayload | FailedResponse

interface FetchCompaniesSuccessResponse {
  succeeded: true
  payload: {
    companies: AdminCompanyResponse[]
  }
}

export type FetchCompaniesResponse = FetchCompaniesSuccessResponse | FailedResponse

interface FetchCompanyUsersSuccessResponse {
  succeeded: true
  payload: {
    users: AdminCompanyUserResponse[]
  }
}

export type FetchCompanyUsersResponse = FetchCompanyUsersSuccessResponse | FailedResponse

interface FetchUnassignedUsersSuccessResponse {
  succeeded: true
  payload: {
    users: UnassignedUserResponseItem[]
  }
}

export type FetchUnassignedUsersResponse = FetchUnassignedUsersSuccessResponse | FailedResponse

/* Export(s) */

/**
 * Backend API for companies administration.
 */
export const AdminCompaniesApi = {
  assignUserToCompany,
  createCompany,
  downloadLatestMeterDataTransferCsvFile,
  downloadMeterDataCsvWithinSpecificDates,
  downloadMeterDataCsvStartingFromDate,
  deleteCompany,
  deleteCompanyIntegration,
  deleteUser,
  fetchCompanies,
  fetchCompanyUsers,
  fetchUnassignedUsers,
  getCompanyIntegration,
  updateCompany,
  updateCompanyIntegration
}

export type AdminCompaniesApiType = typeof AdminCompaniesApi

/* API methods */

/**
 * Assign unassigned user or move user from one company to a new company.
 * @param token Access token.
 * @param input Input data.
 */
async function assignUserToCompany(
  token: string,
  input: {
    companyId: string
    userId: string
  }
): Promise<CompanyUserAssignationResponse> {
  const apiDesc = 'assign user to company request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${input.companyId}/users`)
  const requestBody = {
    userId: input.userId
  }

  try {
    const response = await ApiUtil.doPostRequest(url, requestBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminCompanyUserResponse(payload)
    return {
      succeeded: true,
      payload: {
        user: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend API call to create a new company.
 * @param token Access token.
 * @param input Input data.
 */
async function createCompany(token: string, input: UpdateCompanyRequestPayload): Promise<UpdateCompanyResponse> {
  const apiDesc = 'create company request'
  const url = ApiUtil.getApiUrl(ADMIN_COMPANY_API_PATH)
  const requestBody = {
    ...input
  }

  try {
    const response = await ApiUtil.doPostRequest(url, requestBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminCompanyResponse(payload)
    return {
      succeeded: true,
      payload: {
        company: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend API call to delete an existing company.
 * @param token Access token.
 * @param companyId ID of the company to be deleted.
 */
async function deleteCompany(token: string, companyId: string): Promise<CompanyDeleteResponse> {
  const apiDesc = 'delete company request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}`)

  return await ApiUtil.execDeleteRequest(url, token, apiDesc)
}

/**
 * Backend Admin company API call to delete company integration details for company.
 * @param token Access token.
 * @param companyId Target company ID.
 */
async function deleteCompanyIntegration(
  token: string,
  companyId: string
): Promise<CompanyIntegrationDeleteResponse> {
  const apiDesc = 'delete company integration request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}/integration`)

  try {
    const response = await ApiUtil.doDeleteRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    return {
      succeeded: true
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend Admin company API call to delete company user.
 * @param token Access token.
 * @param input Request input data.
 */
async function deleteUser(
  token: string,
  input: { companyId: string, userId: string }
): Promise<CompanyUserDeleteResponse> {
  const { companyId, userId } = input

  const apiDesc = 'delete company user request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}/users/${userId}`)

  try {
    const response = await ApiUtil.doDeleteRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    return {
      succeeded: true
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend Admin company API call to download devices list file for specific company.
 * @param token Access token.
 * @param companyId ID of the target company.
 */
async function downloadLatestMeterDataTransferCsvFile(token: string, companyId: string): Promise<DownloadFileResponse> {
  const apiDesc = 'download latest meter data transfer CSV file as admin request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}/download`)

  return await ApiUtil.downloadCsvFile({ url, token, apiDesc })
}

async function downloadMeterDataCsvWithinSpecificDates(
  token: string,
  companyId: string,
  payload: DateRange
): Promise<DownloadFileResponse> {
  const apiDesc = 'download meter data withing specific dates admin request'
  const requestBody = {
    type: CsvType.DATE_SPECIFIC,
    startDate: ApiUtil.formatEpochSecondsToDateString(payload.startDate),
    endDate: ApiUtil.formatEpochSecondsToDateString(payload.endDate)
  }

  const url = ApiUtil.getApiUrl(
        `${ADMIN_COMPANY_API_PATH}/${companyId}/meterData`)

  return await ApiUtil.postDownloadFileRequest({ url, token, apiDesc, requestBody })
}

async function downloadMeterDataCsvStartingFromDate(
  token: string,
  companyId: string,
  input: { startDate: number, downloadType: CsvType }
): Promise<DownloadFileResponse> {
  const apiDesc = 'download meter data csv starting from date admin request'
  const url = ApiUtil.getApiUrl(
        `${ADMIN_COMPANY_API_PATH}/${companyId}/meterData`)

  const requestBody = {
    type: input.downloadType,
    startDate: ApiUtil.formatEpochSecondsToDateString(input.startDate)
  }

  return await ApiUtil.postDownloadFileRequest({ url, token, apiDesc, requestBody })
}

/**
 * Backend Admin company API call to fetch all companies.
 * @param token Access token.
 */
async function fetchCompanies(token: string): Promise<FetchCompaniesResponse> {
  const apiDesc = 'fetch companies request'
  const url = ApiUtil.getApiUrl(ADMIN_COMPANY_API_PATH)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminCompaniesResponse(payload)
    return {
      succeeded: true,
      payload: {
        companies: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend Admin company API call to fetch all users linked to a specific company.
 * @param token Access token.
 * @param companyId Target company ID.
 */
async function fetchCompanyUsers(token: string, companyId: string): Promise<FetchCompanyUsersResponse> {
  const apiDesc = 'fetch company users request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}/users`)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminCompanyUsersResponse(payload)
    return {
      succeeded: true,
      payload: {
        users: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend Admin company API call to fetch all unassigned users.
 * @param token Access token.
 */
async function fetchUnassignedUsers(token: string): Promise<FetchUnassignedUsersResponse> {
  const apiDesc = 'fetch unassigned users request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/unassigned_users`)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminUnassignedUsersResponse(payload)
    return {
      succeeded: true,
      payload: {
        users: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend Admin company API call to get company integration details for a company.
 * @param token Access token.
 * @param companyId Target company ID.
 */
async function getCompanyIntegration(
  token: string,
  companyId: string
): Promise<CompanyIntegrationResponse> {
  const apiDesc = 'get company integration request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}/integration`)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminCompanyIntegrationResponse(payload)
    return {
      succeeded: true,
      payload: {
        integration: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend API call to update an exiting company.
 * @param token Access token.
 * @param companyId Target company ID.
 * @param input Update input data.
 */
async function updateCompany(token: string, companyId: string, input: UpdateCompanyRequestPayload): Promise<UpdateCompanyResponse> {
  const apiDesc = 'update company request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}`)
  const requestBody = {
    ...input
  }

  try {
    const response = await ApiUtil.doPutRequest(url, requestBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminCompanyResponse(payload)
    return {
      succeeded: true,
      payload: {
        company: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Backend Admin company API call to update company integration details.
 * @param token Access token.
 * @param companyId Target company ID.
 * @param payload Company Integration details to update.
 */
async function updateCompanyIntegration(
  token: string,
  companyId: string,
  payload: UpdateCompanyIntegrationPayload
): Promise<CompanyIntegrationResponse> {
  const apiDesc = 'update company integration request'
  const url = ApiUtil.getApiUrl(`${ADMIN_COMPANY_API_PATH}/${companyId}/integration`)
  const requestBody = {
    ...payload,
    //  convert integration type to value (uppercase representation)
    integrationType: CompanyIntegrationType[payload.integrationType]
  }

  try {
    const response = await ApiUtil.doPutRequest(url, requestBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminCompanyIntegrationResponse(payload)
    return {
      succeeded: true,
      payload: {
        integration: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}
